/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * هذا الملف جزء من منظومة Madar.iq البرمجية.
 * يُحظر نسخه أو تعديله أو توزيعه دون إذن كتابي.
 * All Rights Reserved — Unauthorized use is prohibited.
 */
import React, { useEffect, useState } from "react";
import {
  Box,
  SimpleGrid,
  Text,
  Badge,
  VStack,
  HStack,
  Divider,
  Button,
  Center,
  Spinner,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Input,
  FormControl,
  FormLabel,
  Switch,
  Textarea,
  Icon,
  Flex,
  IconButton,
  Tooltip,
} from "@chakra-ui/react";
import {
  FaEdit,
  FaPlus,
  FaTrash,
  FaCheckCircle,
  FaTimesCircle,
  FaLayerGroup,
  FaTags,
} from "react-icons/fa";
import { FiCheck } from "react-icons/fi";
import Swal from "sweetalert2";
import { useTheme } from "../../hooks/useTheme";
import PageContainer from "../../components/PageContainer";
import { useSuperAdminPermissions } from "../../../hooks/useSuperAdminPermissions";
import NoAccess from "../../components/NoAccess";

const formatPlanPrice = (value) => {
  const num = Number(value);
  if (Number.isNaN(num)) return "0.000";
  return num.toLocaleString("en-US", {
    minimumFractionDigits: 3,
    maximumFractionDigits: 3,
  });
};

const EMPTY_PLAN = {
  planId: 0,
  planType: "",
  engPlanType: "",
  price: 0,
  descripsion: "",
  engDescripsion: "",
  isActive: 1,
  note1: "", note2: "", note3: "", note4: "", note5: "", note6: "", note7: "", note8: "",
  engNote1: "", engNote2: "", engNote3: "", engNote4: "", engNote5: "", engNote6: "", engNote7: "", engNote8: "",
};

const mapPlan = (p, safeString) => {
  const id = p.planId || p.id || 0;
  return {
    planId: id,
    planType: safeString(p.planType),
    engPlanType: safeString(p.engPlanType),
    price: parseFloat(p.price) || 0,
    descripsion: safeString(p.descripsion),
    engDescripsion: safeString(p.engDescripsion),
    isActive: p.isActive ?? 1,
    note1: safeString(p.note1), note2: safeString(p.note2), note3: safeString(p.note3),
    note4: safeString(p.note4), note5: safeString(p.note5), note6: safeString(p.note6),
    note7: safeString(p.note7), note8: safeString(p.note8),
    engNote1: safeString(p.engNote1), engNote2: safeString(p.engNote2),
    engNote3: safeString(p.engNote3), engNote4: safeString(p.engNote4),
    engNote5: safeString(p.engNote5), engNote6: safeString(p.engNote6),
    engNote7: safeString(p.engNote7), engNote8: safeString(p.engNote8),
    creationDate: p.creationDate || "",
  };
};

const inputSx = (colors) => ({
  bg: colors.inputBg,
  color: colors.textColor,
  borderColor: colors.inputBorder,
  _focus: { borderColor: colors.inputFocus, boxShadow: `0 0 0 1px ${colors.inputFocus}` },
  _hover: { borderColor: colors.inputFocus },
  sx: {
    "&": {
      bg: `${colors.inputBg} !important`,
      color: `${colors.textColor} !important`,
    },
  },
});

export default function Plans() {
  const { isArabic, colors, dir, isDark } = useTheme();
  const toast = useToast();

  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const baseUrl = (process.env.REACT_APP_API_BASE_URL || "https://madar-api.madar.iq/api").replace(/\/+$/, "");
  const perms = useSuperAdminPermissions();

  const safeString = (val) => {
    if (val === null || val === undefined || typeof val === "object") return "";
    return String(val).trim();
  };

  const refreshPlans = async () => {
    const token = localStorage.getItem("authToken");
    const res = await fetch(`${baseUrl}/Plans/GetPlan?PlanId=-1`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: token ? `Bearer ${token}` : "",
      },
    });
    if (!res.ok) throw new Error("Failed to fetch plans");
    const data = await res.json();
    if (!Array.isArray(data)) {
      setPlans([]);
      return;
    }
    const uniquePlans = [];
    const seenIds = new Set();
    data.forEach((p) => {
      const mapped = mapPlan(p, safeString);
      if (mapped.planId && !seenIds.has(mapped.planId)) {
        seenIds.add(mapped.planId);
        uniquePlans.push(mapped);
      }
    });
    setPlans(uniquePlans);
  };

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        await refreshPlans();
      } catch (err) {
        console.error(err);
        toast({ title: isArabic ? "فشل تحميل الباقات" : "Failed to load plans", status: "error" });
      } finally {
        setLoading(false);
      }
    };
    fetchPlans();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isArabic, baseUrl]);

  if (!perms.viewAdminPlansPage) {
    return <NoAccess />;
  }

  const openCreateModal = () => {
    setSelectedPlan({ ...EMPTY_PLAN });
    setIsModalOpen(true);
  };

  const handleSave = async () => {
    if (!selectedPlan.planType?.trim() && !selectedPlan.engPlanType?.trim()) {
      toast({ title: isArabic ? "اسم الباقة مطلوب" : "Plan name is required", status: "error" });
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem("authToken");

      const payload = {
        ...(selectedPlan.planId > 0 && { planId: selectedPlan.planId }),
        planType: selectedPlan.planType || "",
        engPlanType: selectedPlan.engPlanType || "",
        price: selectedPlan.price === "" || selectedPlan.price == null
          ? 0
          : Number(selectedPlan.price) || 0,
        descripsion: selectedPlan.descripsion || "",
        engDescripsion: selectedPlan.engDescripsion || "",
        isActive: selectedPlan.isActive,
        note1: selectedPlan.note1 || "", note2: selectedPlan.note2 || "",
        note3: selectedPlan.note3 || "", note4: selectedPlan.note4 || "",
        note5: selectedPlan.note5 || "", note6: selectedPlan.note6 || "",
        note7: selectedPlan.note7 || "", note8: selectedPlan.note8 || "",
        engNote1: selectedPlan.engNote1 || "", engNote2: selectedPlan.engNote2 || "",
        engNote3: selectedPlan.engNote3 || "", engNote4: selectedPlan.engNote4 || "",
        engNote5: selectedPlan.engNote5 || "", engNote6: selectedPlan.engNote6 || "",
        engNote7: selectedPlan.engNote7 || "", engNote8: selectedPlan.engNote8 || "",
      };

      const res = await fetch(`${baseUrl}/Plans/SavePlan`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token ? `Bearer ${token}` : "",
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const errorText = await res.text();
        throw new Error(errorText || "Save failed");
      }

      toast({
        title: selectedPlan.planId > 0
          ? (isArabic ? "تم التحديث بنجاح" : "Updated successfully")
          : (isArabic ? "تم الإضافة بنجاح" : "Added successfully"),
        status: "success",
      });

      await refreshPlans();
      setIsModalOpen(false);
    } catch (err) {
      toast({ title: isArabic ? "فشل الحفظ" : "Save failed", description: err.message, status: "error" });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (planId) => {
    if (!planId || planId <= 0) return;

    const result = await Swal.fire({
      title: isArabic ? "تأكيد الحذف" : "Confirm Delete",
      text: isArabic ? "هل أنت متأكد من حذف هذه الباقة نهائيًا؟" : "Are you sure you want to delete this plan permanently?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: isArabic ? "نعم، احذف!" : "Yes, delete!",
      cancelButtonText: isArabic ? "إلغاء" : "Cancel",
    });

    if (!result.isConfirmed) return;

    try {
      const token = localStorage.getItem("authToken");

      const res = await fetch(`${baseUrl}/Plans/DeletePlan?PlanId=${planId}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: token ? `Bearer ${token}` : "",
        },
      });

      if (!res.ok) throw new Error("Delete failed");

      setPlans((prev) => prev.filter((p) => p.planId !== planId));

      Swal.fire({
        title: isArabic ? "تم الحذف!" : "Deleted!",
        text: isArabic ? "تم حذف الباقة بنجاح" : "Plan deleted successfully",
        icon: "success",
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (err) {
      Swal.fire({
        title: isArabic ? "فشل الحذف" : "Delete Failed",
        text: err.message,
        icon: "error",
      });
    }
  };

  const getDisplayName = (plan) =>
    isArabic ? (plan.planType || plan.engPlanType || "—") : (plan.engPlanType || plan.planType || "—");
  const getDisplayDesc = (plan) =>
    isArabic ? (plan.descripsion || plan.engDescripsion || "") : (plan.engDescripsion || plan.descripsion || "");
  const getPlanFeatures = (plan) => {
    const features = [];
    for (let i = 1; i <= 8; i++) {
      const note = isArabic ? plan[`note${i}`] : plan[`engNote${i}`] || plan[`note${i}`];
      if (note) features.push(note);
    }
    return features;
  };

  const activeCount = plans.filter((p) => Number(p.isActive) === 1).length;
  const inactiveCount = plans.length - activeCount;
  const freeCount = plans.filter((p) => !p.price || Number(p.price) === 0).length;

  if (loading) {
    return (
      <Center minH="80vh">
        <Spinner size="xl" color={colors.mainColor} thickness="4px" />
      </Center>
    );
  }

  const fieldProps = inputSx(colors);

  return (
    <PageContainer title={isArabic ? "الخطط" : "Plans"}>
      {/* Stats */}
      <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4} mb={8}>
        {[
          {
            label: isArabic ? "إجمالي الخطط" : "Total Plans",
            value: plans.length,
            icon: FaLayerGroup,
            accent: colors.mainColor,
          },
          {
            label: isArabic ? "نشطة" : "Active",
            value: activeCount,
            icon: FaCheckCircle,
            accent: "#22c55e",
          },
          {
            label: isArabic ? "موقوفة" : "Inactive",
            value: inactiveCount,
            icon: FaTimesCircle,
            accent: "#ef4444",
          },
          {
            label: isArabic ? "مجانية" : "Free",
            value: freeCount,
            icon: FaTags,
            accent: "#3b82f6",
          },
        ].map((stat) => (
          <Box
            key={stat.label}
            bg={colors.cardBg}
            borderRadius="2xl"
            borderWidth="1px"
            borderColor={colors.borderColor}
            p={{ base: 4, md: 5 }}
            boxShadow="sm"
            position="relative"
            overflow="hidden"
          >
            <Box
              position="absolute"
              top={0}
              insetInlineStart={0}
              w="4px"
              h="100%"
              bg={stat.accent}
              borderStartRadius="2xl"
            />
            <HStack justify="space-between" align="flex-start">
              <VStack align="start" spacing={1}>
                <Text fontSize="sm" color={colors.subText} fontWeight="medium">
                  {stat.label}
                </Text>
                <Text fontSize={{ base: "2xl", md: "3xl" }} fontWeight="extrabold" color={colors.textColor} lineHeight="1">
                  {stat.value}
                </Text>
              </VStack>
              <Flex
                w="42px"
                h="42px"
                borderRadius="xl"
                align="center"
                justify="center"
                bg={isDark ? "whiteAlpha.100" : "blackAlpha.50"}
                color={stat.accent}
              >
                <Icon as={stat.icon} boxSize={5} />
              </Flex>
            </HStack>
          </Box>
        ))}
      </SimpleGrid>

      {/* Header actions */}
      <Flex mb={6} align="center" justify="space-between" gap={4} flexWrap="wrap">
        <VStack align="start" spacing={0}>
          <Text fontWeight="bold" fontSize="lg" color={colors.textColor}>
            {isArabic ? "جميع الخطط" : "All Plans"}
          </Text>
          <Text fontSize="sm" color={colors.subText}>
            {isArabic ? "عرض وإدارة باقات الاشتراك" : "View and manage subscription plans"}
          </Text>
        </VStack>
        {perms.addAdminPlan && (
          <Button
            leftIcon={<FaPlus />}
            onClick={openCreateModal}
            bg={colors.buttonBg}
            color={colors.buttonText}
            borderRadius="xl"
            px={6}
            h="44px"
            fontWeight="bold"
            boxShadow="md"
            _hover={{ bg: colors.buttonHover, transform: "translateY(-1px)", boxShadow: "lg" }}
            transition="all 0.2s"
          >
            {isArabic ? "إضافة خطة جديدة" : "Add New Plan"}
          </Button>
        )}
      </Flex>

      {/* Plans grid */}
      {plans.length === 0 ? (
        <Center
          minH="40vh"
          bg={colors.cardBg}
          borderRadius="2xl"
          borderWidth="1px"
          borderColor={colors.borderColor}
          borderStyle="dashed"
          flexDir="column"
          gap={3}
          p={10}
        >
          <Icon as={FaLayerGroup} boxSize={12} color={colors.subText} opacity={0.4} />
          <Text color={colors.subText} fontSize="lg" fontWeight="medium">
            {isArabic ? "لا توجد خطط حالياً" : "No plans yet"}
          </Text>
          {perms.addAdminPlan && (
            <Button
              mt={2}
              leftIcon={<FaPlus />}
              onClick={openCreateModal}
              bg={colors.buttonBg}
              color={colors.buttonText}
              borderRadius="xl"
              _hover={{ bg: colors.buttonHover }}
            >
              {isArabic ? "إنشاء أول خطة" : "Create first plan"}
            </Button>
          )}
        </Center>
      ) : (
        <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing={6}>
          {plans.map((plan) => {
            const features = getPlanFeatures(plan);
            const isActive = Number(plan.isActive) === 1;
            const isFree = !plan.price || Number(plan.price) === 0;

            return (
              <Box
                key={plan.planId}
                bg={colors.cardBg}
                borderRadius="2xl"
                borderWidth="1px"
                borderColor={colors.borderColor}
                boxShadow="md"
                overflow="hidden"
                transition="all 0.3s ease"
                h="100%"
                _hover={{
                  transform: "translateY(-6px)",
                  boxShadow: "xl",
                  borderColor: colors.mainColor,
                }}
                display="flex"
                flexDirection="column"
              >
                {/* Card top accent */}
                <Box
                  h="5px"
                  bgGradient={
                    isActive
                      ? `linear(to-r, ${colors.mainColor}, ${colors.mainColorLight})`
                      : "linear(to-r, gray.400, gray.300)"
                  }
                />

                <VStack align="stretch" spacing={4} p={{ base: 5, md: 6 }} flex={1}>
                  <HStack justify="space-between" align="flex-start">
                    <VStack align="start" spacing={1} flex={1} minW={0}>
                      <Text
                        fontWeight="extrabold"
                        fontSize={{ base: "lg", md: "xl" }}
                        color={colors.textColor}
                        noOfLines={1}
                      >
                        {getDisplayName(plan)}
                      </Text>
                      {(isArabic ? plan.engPlanType : plan.planType) && (
                        <Text fontSize="sm" color={colors.subText} noOfLines={1}>
                          {isArabic ? plan.engPlanType : plan.planType}
                        </Text>
                      )}
                    </VStack>
                    <Badge
                      colorScheme={isActive ? "green" : "red"}
                      borderRadius="full"
                      px={3}
                      py={1}
                      fontSize="xs"
                      flexShrink={0}
                    >
                      {isActive
                        ? (isArabic ? "نشطة" : "Active")
                        : (isArabic ? "موقوفة" : "Inactive")}
                    </Badge>
                  </HStack>

                  <Box
                    bg={isDark ? "whiteAlpha.100" : "gray.50"}
                    borderRadius="xl"
                    px={4}
                    py={3}
                    borderWidth="1px"
                    borderColor={colors.borderColor}
                  >
                    <Text fontSize="xs" color={colors.subText} mb={1} fontWeight="medium">
                      {isArabic ? "السعر" : "Price"}
                    </Text>
                    <HStack align="baseline" spacing={2}>
                      <Text fontSize="2xl" fontWeight="extrabold" color={colors.mainColor}>
                        {isFree ? (isArabic ? "مجاني" : "Free") : formatPlanPrice(plan.price)}
                      </Text>
                      {!isFree && (
                        <Text fontSize="sm" color={colors.subText} fontWeight="semibold">
                          IQD
                        </Text>
                      )}
                    </HStack>
                  </Box>

                  {getDisplayDesc(plan) && (
                    <Text fontSize="sm" color={colors.subText} noOfLines={2} lineHeight="1.6">
                      {getDisplayDesc(plan)}
                    </Text>
                  )}

                  {features.length > 0 && (
                    <VStack align="stretch" spacing={2} flex={1} w="100%">
                      <Text fontSize="xs" fontWeight="bold" color={colors.textColor} letterSpacing="wide">
                        {isArabic ? "المميزات" : "Features"}
                      </Text>
                      {features.map((f, idx) => (
                        <HStack key={idx} spacing={2} align="flex-start">
                          <Flex
                            mt="2px"
                            w="18px"
                            h="18px"
                            borderRadius="full"
                            align="center"
                            justify="center"
                            bg={isDark ? "whiteAlpha.100" : `${colors.mainColor}15`}
                            color={colors.mainColor}
                            flexShrink={0}
                          >
                            <Icon as={FiCheck} boxSize={3} />
                          </Flex>
                          <Text fontSize="sm" color={colors.textColor} lineHeight="1.5">
                            {f}
                          </Text>
                        </HStack>
                      ))}
                    </VStack>
                  )}

                  <Box mt="auto" pt={2} w="100%">
                    <Divider borderColor={colors.dividerColor} mb={4} />
                    <HStack spacing={2} justify="flex-end" minH="32px">
                      {perms.editAdminPlan && (
                        <Tooltip label={isArabic ? "تعديل" : "Edit"} hasArrow>
                          <Button
                            size="sm"
                            leftIcon={<FaEdit />}
                            borderRadius="lg"
                            bg={isDark ? "whiteAlpha.100" : `${colors.mainColor}12`}
                            color={colors.mainColor}
                            _hover={{ bg: isDark ? "whiteAlpha.200" : `${colors.mainColor}22` }}
                            onClick={() => {
                              setSelectedPlan(plan);
                              setIsModalOpen(true);
                            }}
                          >
                            {isArabic ? "تعديل" : "Edit"}
                          </Button>
                        </Tooltip>
                      )}
                      {perms.deleteAdminPlan && (
                        <Tooltip label={isArabic ? "حذف" : "Delete"} hasArrow>
                          <IconButton
                            size="sm"
                            icon={<FaTrash />}
                            borderRadius="lg"
                            colorScheme="red"
                            variant="ghost"
                            aria-label={isArabic ? "حذف" : "Delete"}
                            onClick={() => handleDelete(plan.planId)}
                          />
                        </Tooltip>
                      )}
                    </HStack>
                  </Box>
                </VStack>
              </Box>
            );
          })}
        </SimpleGrid>
      )}

      {/* Create / Edit Modal */}
      {selectedPlan && (
        <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} size="xl" scrollBehavior="inside">
          <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
          <ModalContent
            dir={dir}
            bg={colors.cardBg}
            borderRadius="2xl"
            borderWidth="1px"
            borderColor={colors.borderColor}
            mx={3}
            overflow="hidden"
          >
            <Box
              h="4px"
              bgGradient={`linear(to-r, ${colors.mainColor}, ${colors.mainColorLight})`}
            />
            <ModalHeader
              pr={isArabic ? "48px" : "8"}
              pl={isArabic ? "8" : "48px"}
              color={colors.textColor}
              fontWeight="extrabold"
              pb={2}
            >
              {selectedPlan.planId > 0
                ? (isArabic ? "تعديل الخطة" : "Edit Plan")
                : (isArabic ? "إضافة خطة جديدة" : "Add New Plan")}
            </ModalHeader>
            <ModalCloseButton
              top={4}
              right={isArabic ? 4 : "auto"}
              left={isArabic ? "auto" : 4}
              color={colors.textColor}
            />
            <ModalBody>
              <VStack spacing={5} align="stretch">
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <FormControl>
                    <FormLabel color={colors.textColor}>{isArabic ? "اسم الباقة (عربي)" : "Plan Name (Arabic)"}</FormLabel>
                    <Input
                      value={selectedPlan.planType || ""}
                      onChange={(e) => setSelectedPlan({ ...selectedPlan, planType: e.target.value })}
                      borderRadius="lg"
                      {...fieldProps}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel color={colors.textColor}>{isArabic ? "اسم الباقة (إنجليزي)" : "Plan Name (English)"}</FormLabel>
                    <Input
                      value={selectedPlan.engPlanType || ""}
                      onChange={(e) => setSelectedPlan({ ...selectedPlan, engPlanType: e.target.value })}
                      borderRadius="lg"
                      {...fieldProps}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel color={colors.textColor}>{isArabic ? "الوصف (عربي)" : "Description (Arabic)"}</FormLabel>
                    <Textarea
                      value={selectedPlan.descripsion || ""}
                      onChange={(e) => setSelectedPlan({ ...selectedPlan, descripsion: e.target.value })}
                      rows={3}
                      borderRadius="lg"
                      {...fieldProps}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel color={colors.textColor}>{isArabic ? "الوصف (إنجليزي)" : "Description (English)"}</FormLabel>
                    <Textarea
                      value={selectedPlan.engDescripsion || ""}
                      onChange={(e) => setSelectedPlan({ ...selectedPlan, engDescripsion: e.target.value })}
                      rows={3}
                      borderRadius="lg"
                      {...fieldProps}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel color={colors.textColor}>
                      {isArabic ? "السعر (IQD)" : "Price (IQD)"}
                      {selectedPlan.price !== "" && selectedPlan.price != null && !Number.isNaN(Number(selectedPlan.price))
                        ? ` — ${formatPlanPrice(selectedPlan.price)}`
                        : ""}
                    </FormLabel>
                    <Input
                      type="text"
                      inputMode="decimal"
                      value={selectedPlan.price ?? ""}
                      placeholder="0.000"
                      borderRadius="lg"
                      onChange={(e) => {
                        const raw = e.target.value.replace(",", ".");
                        // يسمح بالأرقام والنقطة العشرية أثناء الكتابة (مثل 50.  أو 50.500)
                        if (raw === "" || /^\d*\.?\d{0,3}$/.test(raw)) {
                          setSelectedPlan({ ...selectedPlan, price: raw });
                        }
                      }}
                      {...fieldProps}
                    />
                  </FormControl>
                  <FormControl display="flex" alignItems="center" justifyContent="space-between" pt={{ md: 8 }}>
                    <FormLabel mb={0} color={colors.textColor}>{isArabic ? "الحالة" : "Status"}</FormLabel>
                    <HStack>
                      <Switch
                        colorScheme="green"
                        isChecked={selectedPlan.isActive === 1}
                        onChange={(e) =>
                          setSelectedPlan({ ...selectedPlan, isActive: e.target.checked ? 1 : 0 })
                        }
                      />
                      <Badge colorScheme={selectedPlan.isActive === 1 ? "green" : "red"} borderRadius="full">
                        {selectedPlan.isActive === 1
                          ? (isArabic ? "نشطة" : "Active")
                          : (isArabic ? "موقوفة" : "Inactive")}
                      </Badge>
                    </HStack>
                  </FormControl>
                </SimpleGrid>

                <Divider borderColor={colors.dividerColor} />

                <Text fontWeight="bold" fontSize="lg" color={colors.textColor}>
                  {isArabic ? "المميزات" : "Features"}
                </Text>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
                    <FormControl key={`note${n}`}>
                      <FormLabel fontSize="sm" color={colors.subText}>
                        {isArabic ? `ميزة ${n} (عربي)` : `Feature ${n} (Arabic)`}
                      </FormLabel>
                      <Input
                        value={selectedPlan[`note${n}`] || ""}
                        onChange={(e) => setSelectedPlan({ ...selectedPlan, [`note${n}`]: e.target.value })}
                        borderRadius="lg"
                        {...fieldProps}
                      />
                    </FormControl>
                  ))}
                  {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
                    <FormControl key={`engNote${n}`}>
                      <FormLabel fontSize="sm" color={colors.subText}>
                        {isArabic ? `ميزة ${n} (إنجليزي)` : `Feature ${n} (English)`}
                      </FormLabel>
                      <Input
                        value={selectedPlan[`engNote${n}`] || ""}
                        onChange={(e) => setSelectedPlan({ ...selectedPlan, [`engNote${n}`]: e.target.value })}
                        borderRadius="lg"
                        {...fieldProps}
                      />
                    </FormControl>
                  ))}
                </SimpleGrid>
              </VStack>
            </ModalBody>
            <ModalFooter gap={3} borderTopWidth="1px" borderColor={colors.borderColor}>
              <Button
                variant="ghost"
                onClick={() => setIsModalOpen(false)}
                color={colors.textColor}
                borderRadius="xl"
                _hover={{ bg: colors.hoverBg }}
              >
                {isArabic ? "إلغاء" : "Cancel"}
              </Button>
              <Button
                onClick={handleSave}
                isLoading={saving}
                bg={colors.buttonBg}
                color={colors.buttonText}
                borderRadius="xl"
                px={8}
                _hover={{ bg: colors.buttonHover }}
              >
                {isArabic ? "حفظ" : "Save"}
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}
    </PageContainer>
  );
}
